import DOMPurify from 'dompurify';

/**
 * Simple Markdown Renderer
 * Converts markdown text to HTML for display in chat messages
 * Uses DOMPurify to sanitize HTML and prevent XSS attacks
 */

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  // Escape HTML entities to prevent issues with raw HTML in content
  const escapeHtml = (text: string): string => {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    };
    return text.replace(/[&<>"']/g, (char) => map[char]);
  };
  
  // Simple markdown to HTML conversion
  const renderMarkdown = (text: string): string => {
    // First, escape all HTML to prevent raw HTML from breaking rendering
    let html = escapeHtml(text);
    
    // Bold: **text** or __text__
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
    
    // Italic: *text* or _text_
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/_(.+?)_/g, '<em>$1</em>');
    
    // Code: `code`
    html = html.replace(/`(.+?)`/g, '<code class="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-sm font-mono">$1</code>');
    
    // Links: [text](url)
    html = html.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" class="text-primary-500 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>');
    
    // Headers (must be at start of line)
    html = html.replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold mt-3 mb-1">$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2 class="text-lg font-semibold mt-4 mb-2">$1</h2>');
    html = html.replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold mt-4 mb-2">$1</h1>');
    
    // Line breaks
    html = html.replace(/\n\n/g, '</p><p class="mt-2">');
    html = html.replace(/\n/g, '<br>');
    
    // Unordered lists: - item or * item
    html = html.replace(/^[\s]*[-*] (.+)$/gm, '<li class="ml-4">• $1</li>');
    
    // Ordered lists: 1. item
    html = html.replace(/^[\s]*\d+\. (.+)$/gm, '<li class="ml-4">$1</li>');
    
    // Wrap in paragraph if doesn't start with a tag
    if (!html.startsWith('<')) {
      html = '<p>' + html + '</p>';
    }
    
    return html;
  };
  
  // Render markdown and sanitize with DOMPurify to prevent XSS
  const htmlContent = renderMarkdown(content);
  const sanitizedHTML = DOMPurify.sanitize(htmlContent, {
    ALLOWED_TAGS: ['strong', 'em', 'code', 'a', 'h1', 'h2', 'h3', 'p', 'br', 'li', 'ul', 'ol'],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    ALLOW_DATA_ATTR: false
  });
  
  return (
    <div 
      className={`markdown-content ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
    />
  );
}

